/**
 * DocumentsList — R45 slice 2 Wave 2.
 *
 * Global workspace-level list of documents. Filter by scope + search.
 * Create → jump to editor. Delete confirms.
 *
 * Uses the same scope model as Diagrams (private / workspace / client /
 * brand / project) — visibility is enforced by the backend.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { FileText, Plus, Search, Trash2, Lock, Globe2, Users, Building2, Package as PackageIcon, Copy } from "lucide-react";

const SCOPE_ICONS = {
  private: Lock, workspace: Globe2, client: Users, brand: PackageIcon, project: Building2,
};

export default function DocumentsList() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newScope, setNewScope] = useState("private");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = {};
      if (scope !== "all") params.scope = scope;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/documents`, { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load documents"));
    } finally { setLoading(false); }
  }, [activeWorkspaceId, scope, q]);

  useEffect(() => { load(); }, [activeWorkspaceId, scope, load]);

  const filtered = useMemo(() => items, [items]);

  const create = async () => {
    if (!newTitle.trim()) return toast.error("Enter a title");
    setCreating(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/documents`, {
        title: newTitle.trim(), scope: newScope,
      });
      nav(`/app/documents/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setCreating(false); }
  };

  const del = async (did) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await api.delete(`/documents/${did}`);
      setItems(items.filter((d) => d.id !== did));
      toast.success("Document deleted");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const duplicate = async (did) => {
    try {
      const { data } = await api.post(`/documents/${did}/duplicate`);
      nav(`/app/documents/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Documents"
        title="Rich-text workspace"
        description="Notion-style pages scoped by workspace, client, brand or project. Search across every document you can see."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4" data-testid="documents-list">
        {/* Create bar */}
        <div className="border p-3 bg-neutral-50 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">New document</div>
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Client onboarding SOP · Q1 strategy notes" className="h-9 rounded-none mt-1" data-testid="documents-new-title" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Scope</div>
            <Select value={newScope} onValueChange={setNewScope}>
              <SelectTrigger className="h-9 rounded-none w-40" data-testid="documents-new-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="workspace">Workspace</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="rounded-none h-9" onClick={create} disabled={creating || !newTitle.trim()} data-testid="documents-create">
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title + body…" className="h-8 rounded-none pl-7 w-64" data-testid="documents-search" />
          </div>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-8 rounded-none w-36" data-testid="documents-scope-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="workspace">Workspace</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="project">Project</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-none h-8" onClick={load}>Refresh</Button>
          <div className="text-xs text-muted-foreground ml-auto">{filtered.length} document{filtered.length === 1 ? "" : "s"}</div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border p-8 text-center text-sm text-muted-foreground">
            <FileText className="w-6 h-6 mx-auto mb-2" />
            No documents yet. Create your first one above.
          </div>
        ) : (
          <div className="border">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-muted-foreground border-b p-2 bg-neutral-50">
              <div className="col-span-5">Title</div>
              <div className="col-span-2">Scope</div>
              <div className="col-span-2">Owner</div>
              <div className="col-span-2 text-right">Words · Updated</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {filtered.map((d) => {
              const Icon = SCOPE_ICONS[d.scope] || Lock;
              return (
                <div key={d.id} className="grid grid-cols-12 items-center p-2 text-xs border-b hover:bg-neutral-50" data-testid={`document-row-${d.id}`}>
                  <button onClick={() => nav(`/app/documents/${d.id}`)} className="col-span-5 text-left flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{d.title}</div>
                      {d.body_text && <div className="text-[10px] text-muted-foreground truncate">{d.body_text}</div>}
                    </div>
                  </button>
                  <div className="col-span-2 flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
                    <Icon className="w-3 h-3" /> {d.scope}
                  </div>
                  <div className="col-span-2 text-[10px] text-muted-foreground truncate">{d.owner_id?.slice(0, 8)}</div>
                  <div className="col-span-2 text-right text-[10px] text-muted-foreground">{d.word_count || 0} · {(d.updated_at || "").slice(0, 10)}</div>
                  <div className="col-span-1 text-right flex items-center justify-end gap-1">
                    <button onClick={() => duplicate(d.id)} className="text-muted-foreground hover:text-foreground" title="Duplicate" data-testid={`document-duplicate-${d.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(d.id)} className="text-rose-600" title="Delete" data-testid={`document-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
